import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SITE, SERVICES } from "@/lib/siteConfig";
import { trackConversion } from "@/lib/gtag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    zip_code: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.zip_code || !form.service) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      trackConversion("quote_form_submit");
      toast.success("Demande reçue ! On vous rappelle rapidement.", {
        description: "Pour une urgence, appelez le " + SITE.phone,
      });
      setForm({ name: "", phone: "", zip_code: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue.", {
        description: "Appelez-nous directement au " + SITE.phone,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F3F4F6] py-20 sm:py-28 reveal"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-600 mb-4">
              — Demande de devis
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-[#0A0A0A]">
              Pas pressé ?<br />
              Laissez vos coordonnées.
            </h2>
            <p className="mt-5 text-lg text-slate-600 max-w-md leading-relaxed">
              On vous rappelle dans l'heure avec un devis clair.
              Pour une urgence immédiate :
            </p>

            <a
              href={SITE.phoneHref}
              data-testid="call-cta-contact"
              className="mt-6 inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-black text-white px-6 py-4 font-display font-bold uppercase tracking-tight transition-all shadow-[0_4px_0_#E63946] hover:shadow-[0_2px_0_#E63946] hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5 text-red-500" strokeWidth={2.5} />
              {SITE.phone}
            </a>

            <div className="mt-10 space-y-3 text-sm text-slate-600">
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                Réponse en moins d'une heure ouvrée
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                Devis gratuit et sans engagement
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                Données non revendues · RGPD
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="quote-form"
            className="lg:col-span-7 bg-white border border-black/10 p-6 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Votre nom *">
                <Input
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Jean Dupont"
                  className="h-12 rounded-none border-black/20 focus-visible:ring-red-600"
                />
              </Field>
              <Field label="Téléphone *">
                <Input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="06 12 34 56 78"
                  className="h-12 rounded-none border-black/20 focus-visible:ring-red-600"
                />
              </Field>
              <Field label="Code postal *">
                <Input
                  data-testid="input-zip"
                  value={form.zip_code}
                  onChange={(e) => update("zip_code", e.target.value)}
                  placeholder="Code postal"
                  className="h-12 rounded-none border-black/20 focus-visible:ring-red-600"
                />
              </Field>
              <Field label="Service *">
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    data-testid="select-service"
                    className="h-12 rounded-none border-black/20 focus:ring-red-600"
                  >
                    <SelectValue placeholder="Choisir un service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.key} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Autre">Autre / Je ne sais pas</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Décrivez votre situation">
                <Textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Porte claquée, clé cassée dans la serrure, souhait de blindage…"
                  rows={4}
                  className="rounded-none border-black/20 focus-visible:ring-red-600"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-button"
              className="mt-7 w-full inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-8 py-5 font-display font-black uppercase tracking-tight text-lg transition-all shadow-[0_5px_0_#991B1B] hover:shadow-[0_2px_0_#991B1B] hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" strokeWidth={2.5} />
                  Envoyer ma demande
                </>
              )}
            </button>

            <p className="mt-4 text-xs text-slate-500 text-center">
              * Champs obligatoires · Aucune prospection, uniquement votre devis.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
